import type {
  Carrier,
  FilterTabConfig,
  Order,
  ProcessingStatus,
} from "@/@types/Orders";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Package,
  RotateCcw,
  Truck,
  XCircle,
} from "lucide-react";
import { createElement } from "react";

// Status de processamento disponíveis
export const PROCESSING_STATUS: ProcessingStatus[] = [
  'pending_order',
  'processing_order',
  'shipped',
  'in_transit',
  'out_for_delivery',
  'delivered',
  'delivery_failed',
  'cancelled_order',
  'returned',
];

// Status de pagamento disponíveis
export const PAYMENT_STATUS = [
  'paid',
  'awaiting_payment',
  'not_authorized',
  'refunded',
] as const;

// Status que podem ser alterados manualmente pelo usuário
export const MANUAL_ORDER_STATUSES: ProcessingStatus[] = [
  'pending_order',
  'processing_order',
  'delivery_failed',
  'cancelled_order',
  'returned',
];

// Ícones para cada status de processamento
export const STATUS_ICONS: Record<ProcessingStatus, React.ReactNode> = {
  pending_order: createElement(Clock, { className: "w-3 h-3" }),
  processing_order: createElement(Package, { className: "w-3 h-3" }),
  shipped: createElement(Truck, { className: "w-3 h-3" }),
  in_transit: createElement(Truck, { className: "w-3 h-3" }),
  out_for_delivery: createElement(Truck, { className: "w-3 h-3" }),
  delivered: createElement(CheckCircle2, { className: "w-3 h-3" }),
  delivery_failed: createElement(AlertCircle, { className: "w-3 h-3" }),
  cancelled_order: createElement(XCircle, { className: "w-3 h-3" }),
  returned: createElement(RotateCcw, { className: "w-3 h-3" }),
};

// Tabs de filtro rápido
export const FILTER_TABS: FilterTabConfig[] = [
  { key: 'all', labelKey: 'orders:filter_all' },
  { key: 'open', labelKey: 'orders:filter_open' },
  { key: 'paid_processing', labelKey: 'orders:filter_paid_processing' },
  { key: 'pending_payment', labelKey: 'orders:filter_pending_payment' },
];

// Regras de transição de status (baseado no backend)
export const STATUS_TRANSITIONS: Record<ProcessingStatus, ProcessingStatus[]> = {
  pending_order: ['processing_order', 'shipped', 'cancelled_order'],
  processing_order: ['shipped', 'cancelled_order'],
  shipped: ['in_transit', 'delivered', 'delivery_failed'],
  in_transit: ['out_for_delivery', 'delivered', 'delivery_failed'],
  out_for_delivery: ['delivered', 'delivery_failed'],
  delivery_failed: ['in_transit', 'out_for_delivery', 'returned'],
  delivered: ['returned'],
  cancelled_order: [],
  returned: [],
};

// Transportadoras disponíveis
export const CARRIERS: Carrier[] = [
  { value: "correios", label: "Correios" },
  { value: "jadlog", label: "Jadlog" },
  { value: "loggi", label: "Loggi" },
  { value: "azul_cargo", label: "Azul Cargo" },
  { value: "total_express", label: "Total Express" },
  { value: "fedex", label: "FedEx" },
  { value: "dhl", label: "DHL" },
  { value: "ups", label: "UPS" },
  { value: "usps", label: "USPS" },
  { value: "tnt", label: "TNT" },
  { value: "aramex", label: "Aramex" },
  { value: "dpd", label: "DPD" },
  { value: "gls", label: "GLS" },
  { value: "ontrac", label: "OnTrac" },
  { value: "lasership", label: "LaserShip" },
  { value: "purolator", label: "Purolator" },
  { value: "canada_post", label: "Canada Post" },
  { value: "royal_mail", label: "Royal Mail" },
  { value: "australia_post", label: "Australia Post" },
  { value: "other", label: "Outra" },
];

// Status que requerem modal especial
export const STATUS_REQUIRING_MODAL: ProcessingStatus[] = [
  'delivery_failed',
  'cancelled_order',
  'returned',
];

/**
 * Retorna os status permitidos para transição a partir do status atual
 * Para integrações automáticas (não-manuais)
 */
export const getAllowedTransitions = (currentStatus: ProcessingStatus): ProcessingStatus[] => {
  return STATUS_TRANSITIONS[currentStatus] || [];
};

/**
 * Retorna todos os status disponíveis exceto o atual
 * Para integrações manuais, permite qualquer transição de status
 */
export const getAllowedTransitionsForManual = (currentStatus: ProcessingStatus): ProcessingStatus[] => {
  return PROCESSING_STATUS.filter(status => status !== currentStatus);
};

/**
 * Retorna os status permitidos baseado no tipo de integração do pedido
 * Se for integração manual, permite qualquer transição
 * Se for integração automática (tb3, eagle_labs, etc), usa as regras de transição
 */
export const getAllowedTransitionsForOrder = (
  currentStatus: ProcessingStatus,
  integrationProvider?: string | null
): ProcessingStatus[] => {
  // Se não tem provider ou é 'manual', permite qualquer transição
  if (!integrationProvider || integrationProvider === 'manual') {
    return getAllowedTransitionsForManual(currentStatus);
  }
  // Para integrações automáticas, usa as regras de transição
  return getAllowedTransitions(currentStatus);
};

/**
 * Retorna os status permitidos para seleção direta no dropdown
 * Exclui status que requerem modal especial (ex: delivery_failed)
 */
export const getAllowedTransitionsForDropdown = (
  currentStatus: ProcessingStatus,
  integrationProvider?: string | null
): ProcessingStatus[] => {
  const allowed = getAllowedTransitionsForOrder(currentStatus, integrationProvider);
  return allowed.filter(status => !STATUS_REQUIRING_MODAL.includes(status));
};

/**
 * Verifica se o pedido pode ser marcado como falha na entrega
 */
export const canMarkAsDeliveryFailed = (
  currentStatus: ProcessingStatus,
  integrationProvider?: string | null
): boolean => {
  const allowed = getAllowedTransitionsForOrder(currentStatus, integrationProvider);
  return allowed.includes('delivery_failed');
};

/**
 * Verifica se o pedido pode ser cancelado
 */
export const canCancelOrder = (
  currentStatus: ProcessingStatus,
  integrationProvider?: string | null
): boolean => {
  const allowed = getAllowedTransitionsForOrder(currentStatus, integrationProvider);
  return allowed.includes('cancelled_order');
};

/**
 * Verifica se o pedido pode ser devolvido
 */
export const canReturnOrder = (
  currentStatus: ProcessingStatus,
  integrationProvider?: string | null
): boolean => {
  const allowed = getAllowedTransitionsForOrder(currentStatus, integrationProvider);
  return allowed.includes('returned');
};

/**
 * Verifica se o pedido pode ser cancelado ou devolvido
 */
export const canCancelOrReturn = (
  currentStatus: ProcessingStatus,
  integrationProvider?: string | null
): boolean => {
  return canCancelOrder(currentStatus, integrationProvider) || canReturnOrder(currentStatus, integrationProvider);
};

/**
 * Verifica se uma transição de status é permitida
 */
export const canTransitionTo = (
  currentStatus: ProcessingStatus,
  targetStatus: ProcessingStatus
): boolean => {
  const allowedTransitions = STATUS_TRANSITIONS[currentStatus];
  return allowedTransitions?.includes(targetStatus) ?? false;
};

/**
 * Retorna os status permitidos para múltiplos pedidos (interseção)
 * Exclui status que requerem modal especial
 */
export const getAllowedTransitionsForMultiple = (
  orders: Order[],
  selectedIds: string[]
): ProcessingStatus[] => {
  const selectedOrders = orders.filter(o => selectedIds.includes(o.id));
  if (selectedOrders.length === 0) return [];

  const allowedSets = selectedOrders.map(o =>
    new Set(getAllowedTransitionsForDropdown(o.processing_status, o.integration_provider))
  );
  const intersection = Array.from(allowedSets[0]).filter(status =>
    allowedSets.every(set => set.has(status))
  );

  return intersection;
};

/**
 * Verifica se um status é final (não permite mais transições)
 */
export const isFinalStatus = (status: ProcessingStatus): boolean => {
  return STATUS_TRANSITIONS[status]?.length === 0;
};
